import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  Checkbox,
  Icon,
  type IconKind,
  Loader,
  Menu,
  type MenuOption,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { TOKENS } from '../../../../shared/constants';
import useDebounce from '../../../../shared/hooks/useDebounce';
import { useIdentityOptions } from '../../hooks/useAgentPolicy';
import {
  createEmptyOwnerRow,
  DEFAULT_OWNERS,
  isOwnerRowValid,
  type OwnerRow,
} from '../../utils/policyFormUtils';

export type { OwnerRow };

interface ActionContentProps {
  /** When provided, component is controlled (e.g. from PolicyForm) */
  owners?: OwnerRow[];
  onOwnerChange?: (owners: OwnerRow[]) => void;
  /** When true, show validation message if any row has empty role/rank/owner */
  saveAttempted?: boolean;
  /** Whether to run this policy against existing matching agents (batchModeEnabled) */
  applyToExisting?: boolean;
  /** Callback when the "apply to existing agents" checkbox changes */
  onApplyToExistingChange?: (checked: boolean) => void;
  /** Cached count of agents matching the current conditions (fetched by parent) */
  matchingAgentCount?: number;
}

/** Rank options: label and value as strings so Menu's filter (label.toLowerCase) works */
const rankOptions: MenuOption[] = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

export default function ActionContent({
  owners: ownersProp,
  onOwnerChange,
  saveAttempted = false,
  applyToExisting = false,
  onApplyToExistingChange,
  matchingAgentCount = 0,
}: ActionContentProps = {}) {
  const intl = useIntl();
  const [internalOwners, setInternalOwners] =
    useState<OwnerRow[]>(DEFAULT_OWNERS);
  const owners = ownersProp ?? internalOwners;
  const setOwners = (updater: (prev: OwnerRow[]) => OwnerRow[]) => {
    const next = updater(owners);
    if (onOwnerChange) {
      onOwnerChange(next);
    } else {
      setInternalOwners(next);
    }
  };
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const debouncedOwnerSearch = useDebounce(ownerSearchTerm, 300);
  const { data: ownerOptions = [], isFetching: isOwnerSearching } =
    useIdentityOptions({
      searchText: debouncedOwnerSearch,
      pageSize: 100,
    });

  const removeOwner = (id: string) => {
    setOwners((prev) => prev.filter((o) => o.id !== id));
  };

  const updateOwner = (id: string, field: keyof OwnerRow, value: string) => {
    setOwners((prev) =>
      prev.map((o) => (o.id === id ? { ...o, [field]: value } : o))
    );
  };

  const addOwner = () => {
    setOwners((prev) => [...prev, createEmptyOwnerRow()]);
  };

  const hasIncompleteOwner = owners.some((row) => !isOwnerRowValid(row));
  const showOwnerValidation = saveAttempted && hasIncompleteOwner;

  const duplicateOwnerIds = useMemo(() => {
    const ids = new Set<string>();
    owners.forEach((row) => {
      if (!row.role || !row.rank || !row.owner) {
        return;
      }
      const isDuplicate = owners.some(
        (other) =>
          other.id !== row.id &&
          other.role === row.role &&
          other.rank === row.rank &&
          other.owner === row.owner
      );
      if (isDuplicate) {
        ids.add(row.id);
      }
    });
    return ids;
  }, [owners]);

  const businessOwnerCount = owners.filter((o) => o.role === 'business').length;
  const technicalOwnerCount = owners.filter(
    (o) => o.role === 'technical'
  ).length;
  const isAddOwnerDisabled =
    owners.length >= 20 ||
    (businessOwnerCount >= 10 && technicalOwnerCount >= 10);

  const options = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: 'M10l5P',
          defaultMessage: 'Business',
          description: 'Business role option label',
        }),
        secondaryLabel: intl.formatMessage({
          id: '5RcDMo',
          defaultMessage:
            "Accountable for the agent's overall purpose, risk, and business alignment.",
          description: 'Business role option description',
        }),
        value: 'business',
        isDisabled: businessOwnerCount >= 10,
      },
      {
        label: intl.formatMessage({
          id: 'PRbyX0',
          defaultMessage: 'Technical',
          description: 'Technical role option label',
        }),
        secondaryLabel: intl.formatMessage({
          id: 'W5LXxw',
          defaultMessage:
            'Responsible for technical setup, integrations, configuration, and ongoing maintenance of the agent.',
          description: 'Technical role option description',
        }),
        value: 'technical',
        isDisabled: technicalOwnerCount >= 10,
      },
    ],
    [intl, businessOwnerCount, technicalOwnerCount]
  );

  const handleRoleChange =
    (ownerId: string) => (opt: { value: string } | null) => {
      if (opt) {
        updateOwner(ownerId, 'role', opt.value);
      }
    };

  const handleRankChange = (ownerId: string) => (opt: MenuOption | null) => {
    if (opt) {
      updateOwner(ownerId, 'rank', String(opt.value));
    }
  };

  const handleOwnerChange = (ownerId: string) => (opt: MenuOption | null) => {
    if (opt) {
      setOwners((prev) =>
        prev.map((o) =>
          o.id === ownerId
            ? {
                ...o,
                owner: String(opt.value),
                ownerLabel: String(opt.label),
                ownerEmail:
                  (opt as MenuOption & { secondaryLabel?: string })
                    .secondaryLabel ?? '',
              }
            : o
        )
      );
    }
  };

  return (
    <Box tag='main' className='PolicyForm-detail'>
      {/* Panel header: Assign owners */}
      <Box
        className={cx(
          'PolicyForm-panel-surface',
          'PolicyForm-panel-header',
          'PolicyForm-panel-header--actions'
        )}>
        <Icon kind='User' size='medium' color={TOKENS.CF_SEC_BLD} />
        <Typography kind='h2' color={TOKENS.CF_SEC_BLD}>
          <FormattedMessage
            id='UNUMDP'
            defaultMessage='Assign owners'
            description='Assign owners panel header'
          />
        </Typography>
      </Box>

      {/* Panel body */}
      <Box className={cx('PolicyForm-panel-surface', 'PolicyForm-panel-body')}>
        <Box className='PolicyForm-owners-section-title'>
          <Typography
            kind='h3'
            desktop='h3-d'
            mobile='h3-d'
            color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='Ey7gaM'
              defaultMessage='Initial Assignment'
              description='Initial assignment section title'
            />
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color={TOKENS.CF_NEU_STL}>
            <FormattedMessage
              id='UB7m/o'
              defaultMessage='These owners will be assigned when the rule runs.'
              description='Initial assignment section description'
            />
          </Typography>
        </Box>
        {showOwnerValidation && (
          <Typography kind='body2' color={TOKENS.CF_CRT_BLD}>
            <FormattedMessage
              id='UXbSO+'
              defaultMessage='Complete all owner rows: select a role, rank, and owner for each.'
              description='Validation message when save attempted with incomplete owners'
            />
          </Typography>
        )}

        {/* Table: header row */}
        <Box className='PolicyForm-owners-table-wrapper'>
          <Box className='PolicyForm-owners-table'>
            <Box className='PolicyForm-owners-table-header'>
              <Box className='PolicyForm-owners-table-headerCell'>
                <Typography kind='body3' color={TOKENS.CF_NEU_BLD}>
                  <FormattedMessage
                    id='WALxFw'
                    defaultMessage='ROLE'
                    description='Role column header'
                  />
                </Typography>
                <Tooltip
                  text={intl.formatMessage({
                    id: '9J/5Tc',
                    defaultMessage:
                      'Role defines the type of responsibility. Business Owners oversee governance and access decisions. Technical Owners manage configuration and operational support.',
                    description: 'Role info tooltip',
                  })}
                  isPortalEnabled>
                  <Box className='PolicyForm-owners-tooltip-trigger'>
                    <Icon
                      kind={'AlertInfoFilled' as IconKind}
                      size='small'
                      color={TOKENS.CF_INF_BLD}
                    />
                  </Box>
                </Tooltip>
              </Box>
              <Box className='PolicyForm-owners-table-headerCell'>
                <Typography kind='body3' color={TOKENS.CF_NEU_BLD}>
                  <FormattedMessage
                    id='G2XSTH'
                    defaultMessage='RANK'
                    description='Rank column header'
                  />
                </Typography>
                <Tooltip
                  text={intl.formatMessage({
                    id: 'dKkQfb',
                    defaultMessage:
                      'Rank sets priority within the same role. Rank 1 is the primary contact. Lower ranks provide backup and support succession.',
                    description: 'Rank info tooltip',
                  })}
                  isPortalEnabled>
                  <Box className='PolicyForm-owners-tooltip-trigger'>
                    <Icon
                      kind={'AlertInfoFilled' as IconKind}
                      size='small'
                      color={TOKENS.CF_INF_BLD}
                    />
                  </Box>
                </Tooltip>
              </Box>
              <Box
                className={cx(
                  'PolicyForm-owners-table-headerCell',
                  'PolicyForm-owners-table-headerCell--isNoGap'
                )}>
                <Typography kind='body3' color={TOKENS.CF_NEU_BLD}>
                  <FormattedMessage
                    id='O2JFTM'
                    defaultMessage='OWNER'
                    description='Owner column header'
                  />
                </Typography>
              </Box>
              <Box className='PolicyForm-owners-table-headerSpacer' />
            </Box>

            {/* Owner rows */}
            {owners.map((owner) => (
              <Box key={owner.id} className='PolicyForm-owners-table-row'>
                <Box className='PolicyForm-owners-table-rowGrid'>
                  <Box className='PolicyForm-owners-table-cell'>
                    <Menu
                      kind='simple'
                      isPortalEnabled
                      placement='bottom'
                      options={options}
                      onChange={handleRoleChange(owner.id)}
                      classNames={{
                        dropdownClassName: 'PolicyForm-roleMenuDropdown',
                        customMenuClassName: 'PolicyForm-roleMenuDropdown',
                      }}
                      dataTestId='PolicyForm-roleMenuDropdown'
                      trigger={
                        <ButtonSelect
                          kind='rectangle'
                          size='medium'
                          color='neutral'
                          label={
                            (options.find((o) => o.value === owner.role)
                              ?.label ??
                              owner.role) ||
                            intl.formatMessage({
                              id: '+1IneH',
                              defaultMessage: 'Select role',
                              description: 'Role dropdown placeholder',
                            })
                          }
                          isVisualCueEnabled
                          className='PolicyForm-owners-roleBtn'
                        />
                      }
                      shouldCloseMenuOnItemClick
                    />
                  </Box>
                  <Box className='PolicyForm-owners-table-cell'>
                    <Menu
                      kind='simple'
                      isPortalEnabled
                      placement='bottom'
                      options={rankOptions}
                      onChange={handleRankChange(owner.id)}
                      trigger={
                        <ButtonSelect
                          kind='rectangle'
                          size='medium'
                          color='neutral'
                          label={
                            (rankOptions.find((o) => o.value === owner.rank)
                              ?.label ??
                              owner.rank) ||
                            intl.formatMessage({
                              id: 'M2P67H',
                              defaultMessage: 'Rank',
                              description: 'Rank dropdown placeholder',
                            })
                          }
                          isVisualCueEnabled
                          className='PolicyForm-owners-roleBtn'
                        />
                      }
                      shouldCloseMenuOnItemClick
                    />
                  </Box>
                  <Box className='PolicyForm-owners-table-cell'>
                    <Menu
                      kind='simple'
                      isPortalEnabled
                      placement='bottom'
                      options={isOwnerSearching ? [] : ownerOptions}
                      onChange={handleOwnerChange(owner.id)}
                      onSearchTermChange={setOwnerSearchTerm}
                      setIsExpanded={(isExpanded: boolean) => {
                        if (!isExpanded) {
                          setOwnerSearchTerm('');
                        }
                      }}
                      minOptionsForSearch={0}
                      classNames={{
                        dropdownClassName: 'PolicyForm-roleMenuDropdown',
                        customMenuClassName: 'PolicyForm-roleMenuDropdown',
                      }}
                      footerComp={
                        isOwnerSearching ? (
                          <Box className='SidePanelPolicyRuleSummary-loaderRow'>
                            <Loader
                              kind='dots'
                              format='inline'
                              size='small'
                              color='brand'
                            />
                          </Box>
                        ) : undefined
                      }
                      trigger={
                        <ButtonSelect
                          kind='rectangle'
                          size='medium'
                          color='neutral'
                          label={
                            owner.ownerLabel ??
                            ownerOptions.find((o) => o.value === owner.owner)
                              ?.label ??
                            (owner.owner ||
                              intl.formatMessage({
                                id: 'Jz+7Hh',
                                defaultMessage: 'Select owner',
                                description: 'Owner dropdown placeholder',
                              }))
                          }
                          isVisualCueEnabled
                          className='PolicyForm-owners-roleBtn'
                        />
                      }
                      shouldCloseMenuOnItemClick
                    />
                  </Box>
                  <Box className='PolicyForm-owners-table-removeCell'>
                    <ButtonIcon
                      size='medium'
                      kind='ghost'
                      className='PolicyForm-delete-btn'
                      icon={<Icon kind='Delete' size='small' />}
                      ariaLabel={intl.formatMessage({
                        id: 'nfjMxA',
                        defaultMessage: 'Remove owner',
                        description: 'Remove owner row',
                      })}
                      onClick={() => removeOwner(owner.id)}
                    />
                  </Box>
                </Box>
                {duplicateOwnerIds.has(owner.id) && (
                  <Typography
                    kind='body3'
                    color={TOKENS.CF_CRT_BLD}
                    className='PolicyForm-owners-duplicateMessage'>
                    <FormattedMessage
                      id='beMUjS'
                      defaultMessage='You have already assigned this owner to same role and rank'
                      description='Duplicate owner row error message'
                    />
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <Button
            kind='outlined'
            size='small'
            leftIcon={<Icon kind='Add' size='small' />}
            onClick={addOwner}
            isDisabled={isAddOwnerDisabled}>
            <FormattedMessage
              id='mo65Hi'
              defaultMessage='Add Another Owner'
              description='Add another owner button'
            />
          </Button>
        </Box>
      </Box>

      {/* Apply to existing agents checkbox */}
      <Box
        className={cx(
          'PolicyForm-panel-surface',
          'PolicyForm-applyExistingRow'
        )}>
        <Checkbox
          kind='singleLine'
          size='medium'
          label={
            <span className='PolicyForm-applyExistingLabel'>
              <FormattedMessage
                id='4M9pDL'
                defaultMessage='Apply to existing {count} matching agents'
                description='Checkbox label to apply policy to existing matching agents'
                values={{ count: matchingAgentCount ?? 0 }}
              />
              <Tooltip
                text={intl.formatMessage(
                  {
                    id: 's1l4LS',
                    defaultMessage:
                      'Once you activate this rule, it will run immediately against all {count} active agents that currently match the defined conditions.',
                    description:
                      'Tooltip for apply to existing agents checkbox',
                  },
                  { count: matchingAgentCount ?? 0 }
                )}
                isPortalEnabled
                classNames={{
                  containerClassName:
                    'PolicyForm-applyExistingTooltipContainer',
                }}>
                <span className='PolicyForm-applyExistingTooltipTrigger'>
                  <Icon
                    kind='AlertInfoFilled'
                    size='small'
                    color={TOKENS.CF_INF_MED}
                  />
                </span>
              </Tooltip>
            </span>
          }
          textPosition='after'
          isChecked={applyToExisting}
          onChange={() => onApplyToExistingChange?.(!applyToExisting)}
        />
      </Box>
    </Box>
  );
}
